import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import { Virtuoso } from 'react-virtuoso'
// evertel
import { Text, Modal, ModalBody, ModalHeader, InfoBox, Spinner } from '@evertel/web/ui'
import { UserTile } from '@evertel/web/user'
import { useService } from '@evertel/di'
import { MessageReactedByController } from '@evertel/message'
import { APIDataReactionUserThrough } from '@evertel/types'
import { Emoji } from '@evertel/web/emojis'

interface ReactedByModalProps {
    messageId: number
    modelType: 'room'|'thread'
    visible: boolean
    onClose: () => void
    onCloseDone: () => void
}


const ReactedByModal: React.FC<ReactedByModalProps> = observer(({
    messageId,
    modelType,
    visible,
    onClose,
    onCloseDone
}) => {

    const reactedByController = useService(MessageReactedByController, [messageId])

    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        (async () => {
            if (visible) {
                setIsLoading(true)

                await reactedByController.init(messageId, modelType)

                setIsLoading(false)
            }
        })()
    }, [messageId, visible, reactedByController, modelType])

    const onEndReached = () => {
        if (visible) {
            reactedByController.fetchNextPage()
        }
    }

    const title = (reactedByController.reactedByCount === 1) ? reactedByController.reactedByCount + ' reaction' : reactedByController.reactedByCount + ' reactions'

    return (
        <Modal
            style={{
                height: (131 + reactedByController.reactedByCount * 45),
                maxHeight: '80vh'
            }}
            visible={visible}
            alignment="center"
            onClose={onClose}
            onCloseDone={onCloseDone}>
            <ModalHeader
                title={(isLoading) ? 'Loading reactions...' : title}
                closeButton
            />
            <ModalBody>
                <Virtuoso
                    endReached={onEndReached}
                    data={reactedByController.reactedBy}
                    totalCount={reactedByController.reactedByCount}
                    overscan={300}
                    style={{ height: '100%', overflowX: 'hidden' }}
                    itemContent={(index, reaction) => {
                        return (
                            <UserReactionRow
                                data={reaction}
                                index={index}
                            />
                        )
                    }}
                    components={{
                        Footer: () => {
                            return (
                                <Footer
                                    showSpinner={isLoading}
                                />
                            )
                        },
                        EmptyPlaceholder: () => {
                            return (
                                <EmptyPlaceholder
                                    isLoading={isLoading}
                                />
                            )
                        }
                    }}
                />
            </ModalBody>
        </Modal>
    )
})


interface UserProps {
    index: number
    data: APIDataReactionUserThrough
}

const UserReactionRow: React.FC<UserProps> = ({
    index,
    data
}) => {

    if (!data?.reaction) return null

    return (
        <UserTile
            userId={data.blueUserId}
            className="border-bottom pb-1 mb-1 me-2 align-items-center"
            rightComponent={
                <>
                    <Emoji
                        emoji={data.reaction.text}
                        size={22}
                    />
                    <Text
                        size="smaller"
                        className="ms-2">
                        {moment(data.createdDate).format('L HH:mm:ss')}
                    </Text>
                </>
            }
        />
    )
}

const Footer = ({ showSpinner }) => {
    if (!showSpinner) return null

    return (
        <i>
            <Spinner /> Loading...
        </i>
    )
}

const EmptyPlaceholder = ({ isLoading }) => {
    if (isLoading) return null

    return (
        <InfoBox color="info">
            No one has reacted to this message yet
        </InfoBox>
    )
}

export { ReactedByModal }
